import { useEffect, useMemo, useRef } from "react";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import type { ListParams, OptionItem, PagedResult } from "@/lib/list-query";

/**
 * Big enough that the first page overflows the list, so opening it does not
 * immediately ask for a second one, and small enough that opening it is one
 * cheap request.
 */
const PAGE_SIZE = 20;

/**
 * How far ahead of the end of the list the next page is fetched. Loading before
 * the last row is reached is what keeps arrow-key navigation from stopping dead
 * at a page boundary: by the time the highlight gets there, the rows exist.
 */
const LOAD_AHEAD_PX = 160;

/**
 * How long a loaded page stays good for. Without it every reopen re-downloads
 * every page the list had scrolled through — React Query treats data as stale
 * immediately, and refetching an infinite query refetches all of its pages. The
 * agent mutations invalidate this key, so a create or a rename still shows up.
 */
const OPTIONS_STALE_TIME_MS = 5 * 60 * 1000;

/** Matches the paged screens', so typing feels the same everywhere. */
const SEARCH_DEBOUNCE_MS = 300;

/** Where a lazy picker's options come from — see `agentPickerSource`. */
export interface PagedOptionsSource {
  /** Base React Query key for this source, e.g. `agentKeys.picker(workspaceId)`. */
  queryKey: readonly unknown[];
  /** One page of options. Searching and sorting are the server's job. */
  fetchPage: (params: ListParams) => Promise<PagedResult<OptionItem>>;
}

interface PagedOptionsArgs extends PagedOptionsSource {
  /** The search box's live value; the request waits for typing to pause. */
  search: string;
  /** False parks every request. */
  enabled: boolean;
}

/**
 * The paging behind both lazy pickers, `LazyCombobox` and `LazyChecklist`: a
 * server list read a page at a time, the next page asked for as the end of what
 * is rendered comes into view.
 *
 * The caller attaches the two refs it returns — `listRef` to the element that
 * scrolls, `endRef` to a marker after the last row, rendered only while
 * `hasNextPage`.
 */
export function usePagedOptions({ queryKey, fetchPage, search, enabled }: PagedOptionsArgs) {
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const { data, isFetching, hasNextPage, fetchNextPage, isError } = useInfiniteQuery({
    queryKey: [...queryKey, "lazy-page", debouncedSearch],
    queryFn: ({ pageParam }) =>
      fetchPage({ page: pageParam, size: PAGE_SIZE, search: debouncedSearch }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      // An empty page ends the list whatever the count says. `total` is a
      // separate `count: "exact"` query, so a row deleted between two requests
      // leaves it promising rows that no page will ever return — and asking
      // again for the page that came back empty is an infinite scroll loop.
      if (lastPage.rows.length === 0) return undefined;

      // `total` is the whole match, not this page — the server sends it so the
      // list knows there is more without probing for an empty page.
      const loaded = pages.reduce((count, page) => count + page.rows.length, 0);
      return loaded < lastPage.total ? pages.length + 1 : undefined;
    },
    enabled,
    // Keeps the previous results on screen while a new search loads, so typing
    // does not blank the list.
    placeholderData: keepPreviousData,
    staleTime: OPTIONS_STALE_TIME_MS,
    // A picker is opened deliberately; coming back to the tab is not a reason
    // to re-request every page of it.
    refetchOnWindowFocus: false,
  });

  const options = useMemo(() => data?.pages.flatMap((page) => page.rows) ?? [], [data]);

  const listRef = useRef<HTMLDivElement | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  /**
   * The next page is asked for when the marker at the end of the list comes
   * into view, rather than from a scroll handler. An observer does not care
   * *how* the list moved, so the wheel, the scrollbar, a touch drag and the
   * arrow keys all page the same way — and a page too short to fill the box
   * loads the next one even though no scroll event ever fires.
   *
   * It is rebuilt whenever the rows change, because an observer reports a
   * *change* in visibility and nothing else: once new rows have pushed the
   * marker down it has to be asked again whether the marker is still in view.
   * That is also what chain-loads pages until the box is full. `enabled` is in
   * the list because a popover's rows mount only when it opens.
   */
  useEffect(() => {
    const root = listRef.current;
    const end = endRef.current;
    if (!root || !end || !hasNextPage) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // `cancelRefetch: false`: the default aborts the request already in
        // flight and starts the same page again, which is one wasted round trip
        // per page on any list that pages faster than it answers.
        if (entry.isIntersecting) fetchNextPage({ cancelRefetch: false });
      },
      { root, rootMargin: `0px 0px ${LOAD_AHEAD_PX}px 0px` },
    );

    observer.observe(end);
    return () => observer.disconnect();
  }, [hasNextPage, fetchNextPage, options.length, enabled]);

  return { options, isFetching, hasNextPage, isError, listRef, endRef };
}
